import { useMemo } from 'react'

import { useSearch } from '@tanstack/react-router'

import type { getProductImage } from '@sofa-platform/dexf-utils'
import type { Enriched, EnrichedRangeData, EnrichedRangeDataRecord, SofaItem } from '@sofa-platform/types'

import { useInsights } from '@/hooks/use-insights'
import { useRangeData } from '@/hooks/use-range-data'
import { useSettingsAndTranslations } from '@/hooks/use-settings-and-translations'
import { getLanguageFromPath } from '@/utils/base-path-params'
import { getProductImageOrFallbackProductAltText } from '@/utils/get-product-image-or-fallback-product-alt-text'

import { MODULAR_RANGES, type StartGalleryFilter, widthOptions } from '../filter'

export type RangeGalleryData = {
  hasArmrests?: boolean
  hasLegs?: number
  hasMattresses?: number
  hasOnlySofaBeds: boolean
  hasRemovableCover: boolean
  hasSofabed: boolean
  hasStorage: boolean
  missingSofas?: 'every' | 'some'
  name: string
  numberOfCovers: number
  numberOfSofas: number
  posterImg: ReturnType<typeof getProductImage>
  posterSofa: Enriched<SofaItem>
  rangeId: string
}

export function getFiltersToShow(rangeGalleryData: RangeGalleryData[]) {
  const shouldShowRemovableCover = rangeGalleryData.some((data) => data.hasRemovableCover)
  const shouldShowSofaBed = rangeGalleryData.some((data) => data.hasSofabed)
  const shouldShowStorage = rangeGalleryData.some((data) => data.hasStorage)
  const shouldShowIsModular = rangeGalleryData.some((data) => MODULAR_RANGES.includes(data.rangeId))
  return { shouldShowIsModular, shouldShowRemovableCover, shouldShowSofaBed, shouldShowStorage }
}

export function useRangeGalleryData() {
  const { data: rangeDataRaw } = useRangeData()
  const { dexfSettings } = useSettingsAndTranslations()
  const { sendCustomEvent } = useInsights()
  const isMetric = dexfSettings.localisation.useMetricMeasures
  const {
    dev: isDev,
    hasRemovableCover,
    hasSofabed,
    hasStorage,
    isModular,
    width,
  } = useSearch({
    from: '/$country/$language/navigator/',
  })
  const filter: StartGalleryFilter = useMemo(
    () => ({ hasRemovableCover, hasSofabed, hasStorage, isModular, width }),
    [hasRemovableCover, hasSofabed, hasStorage, width, isModular],
  )

  const rangeGalleryData: RangeGalleryData[] = useMemo(
    () =>
      Object.entries(rangeDataRaw)
        .map(([range, data]) => {
          const { covers, displayName, hasRemovableCover, hasSofabed, hasStorage, sofas } = data
          const hasOnlySofaBeds = sofas.every((sofa) => sofa.hasSofabed)
          const availableCovers = covers.filter((cover) => {
            return sofas.some((sofa) => sofa.cover === cover.name)
          })

          if (getFilterEarlyExit(range, data, filter)) return null

          const posterSofa = getPosterProductData(data, isMetric, filter)

          const hasLegs = data.legs?.length && data.legs.length > 1 ? data.legs.length : undefined
          const hasMattresses =
            data.mattresses?.length && data.mattresses.length > 1 ? data.mattresses.length : undefined
          const hasArmrests = !!data.hasArmrestPicker || undefined

          if (!posterSofa) {
            // biome-ignore lint/suspicious/noConsole: debug tool
            if (import.meta.env.DEV) console.warn(`No poster product found for range ${range}`)
            return null
          }

          return {
            hasArmrests,
            hasLegs,
            hasMattresses,
            hasOnlySofaBeds,
            hasRemovableCover,
            hasSofabed,
            hasStorage,
            missingSofas: hasMissingSofasInCatalog(data, isDev),
            name: displayName,
            numberOfCovers: availableCovers.length,
            numberOfSofas: [...new Set(sofas.map((sofa) => sofa.code))].length,
            posterImg: getProductImageOrFallbackProductAltText(
              posterSofa.productData,
              'S3',
              'Main Product Picture',
              sendCustomEvent,
            ),
            posterSofa,
            rangeId: range,
          }
        })
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name, getLanguageFromPath())),
    [rangeDataRaw, filter, isDev, isMetric, sendCustomEvent],
  )
  return rangeGalleryData
}

function disallowRemovableCoverInLeather(sofa: Enriched<SofaItem>, filter: StartGalleryFilter) {
  const { hasRemovableCover } = filter
  if (!hasRemovableCover) return true
  if (!sofa.childItems?.length) return false
  return !sofa.productData.content.filterAttribute?.some((filterAttr) => filterAttr.valueCode === '38829')
}

function filterOn(key: 'hasSofabed' | 'hasStorage', sofa: Enriched<SofaItem>, filter: StartGalleryFilter) {
  return !filter[key] || sofa[key]
}

function getFilterEarlyExit(rangeId: string, data: EnrichedRangeDataRecord[number], filter?: StartGalleryFilter) {
  const { hasRemovableCover, hasSofabed, hasStorage } = data
  if (filter?.isModular && !MODULAR_RANGES.includes(rangeId)) return true
  if (filter?.hasRemovableCover && !hasRemovableCover) return true
  if (filter?.hasSofabed && !hasSofabed) return true
  if (filter?.hasStorage && !hasStorage) return true
  return false
}

function getPosterProductData(
  rangeData: EnrichedRangeData,
  isMetric: boolean,
  filter?: StartGalleryFilter,
): Enriched<SofaItem> | undefined {
  if (!filter) return rangeData.sofas.at(0)
  const measurementSystem = !isMetric ? 'imperial' : 'metric'
  const { lowerBound, upperBound } = widthOptions[filter.width][measurementSystem]

  return rangeData.sofas
    .filter((sofa) => {
      if (filter.width === 'all') return true
      if (!sofa.widthInMillimeters) return false
      return sofa.widthInMillimeters > lowerBound && sofa.widthInMillimeters < upperBound
    })
    .find((sofa) => {
      return (
        filterOn('hasSofabed', sofa, filter) &&
        filterOn('hasStorage', sofa, filter) &&
        disallowRemovableCoverInLeather(sofa, filter)
      )
    })
}

function hasMissingSofasInCatalog(
  data: EnrichedRangeDataRecord[number],
  isDev: boolean | undefined,
): 'every' | 'some' | undefined {
  if (!isDev) return
  if (data.sofas.every((sofa) => sofa.isMissingBryggaData)) return 'every'
  if (data.sofas.some((sofa) => sofa.isMissingBryggaData)) return 'some'
}
